import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  FACILITY_LAYER_ROOT,
  GEO_MODEL_LAYERS,
  IPDB_CONTROL_WELLS_LAYER,
  IPDB_LAYER,
  POLYGON_BINS_MY_FIELDS_LAYERS,
  POLYGON_BINS_ORG_FIELDS_LAYERS,
  POLYGON_BINS_PROJECT_FIELDS_LAYERS
} from "constants/mapLayers.constants";
import {
  selectIpdbControlWellsLoaded,
  selectIpdbGeoModelLoaded,
  useMapStore
} from "stores/map";

import { useColumns } from "hooks";
import useBetaFeatures from "hooks/useBetaFeatures";

import { IShapefile, IShapefileNode, ShapefileFileType } from "models/projects";

import { groupFields } from "components/groupBy/helpers";
import { GEO_MODEL_TOOLTIP_MESSAGE } from "components/project/layers/constants/projectLayerConstants";
import { useProjectContext } from "components/project/projects/context";
import { useProjectShapefilesQuery } from "components/project/shapefiles/queries";

import { EntityKind } from "../../../../../models/entityKind";
import { RootState } from "../../../../../store/rootReducer";
import {
  GRID_LAYERS_SECTION,
  GRID_LAYER_FOLDERS,
  ProjectTreeNodeT,
  SHAPEFILE_SOURCE,
  SHAPEFILE_TREE_SECTIONS,
  getProjectTreeRootMap
} from "./useProjectLayerTree.types";

export interface UseProjectLayerTreeReturnT {
  error: string;
  isLoading: boolean;
  tree: ProjectTreeNodeT[];
}

export function useProjectLayerTree(): UseProjectLayerTreeReturnT {
  // store values
  const activeEntityKinds = useSelector(
    (state: RootState) => state.app.activeEntityKinds
  );
  const ipdbGeoModelLoaded = useMapStore(selectIpdbGeoModelLoaded);
  const ipdbControlWellsLoaded = useMapStore(selectIpdbControlWellsLoaded);

  const { hasFeature } = useBetaFeatures();
  const rigFracEnabled = hasFeature("Rig And Frac Layers");

  // context
  const { selectedProjectId } = useProjectContext();

  // hooks
  const projectShapefiles = useProjectShapefilesQuery({
    projectId: selectedProjectId
  });

  const columns = useColumns();
  const groupedColumns = groupFields(columns);

  const groupedBins = useMemo(() => {
    if (groupedColumns.size === 0) {
      return [];
    }
    return Array.from(groupedColumns).reduce((acc, [key, list]) => {
      const geomFields = list
        .filter((l) => l.fieldType === "geom")
        .sort((a, b) => a.withinSubgroupOrder - b.withinSubgroupOrder)
        .map((field) => ({
          ...field,
          source: field.property.startsWith("My_Focus_Fields")
            ? "user"
            : field.property.startsWith("Project_Focus_Fields")
            ? "project"
            : "org"
        }));
      if (geomFields.length > 0) {
        acc.push({ key, geomFields });
      }
      return acc;
    }, []);
  }, [groupedColumns]);

  // state
  const [tree, setTree] = useState<ProjectTreeNodeT[]>([]);

  const parseShapefileChildren = (
    children: IShapefileNode[],
    source: SHAPEFILE_SOURCE.PROJECT | SHAPEFILE_SOURCE.ORG | SHAPEFILE_SOURCE.MCDAN
  ): ProjectTreeNodeT[] => {
    const subTree: ProjectTreeNodeT[] = [];
    if (children) {
      children.forEach((node: IShapefileNode, i) => {
        const type = node.type === "node" ? "folder" : "layer";
        const treeNode: ProjectTreeNodeT = {
          type: type,
          originalIndex: i,
          key:
            node.type === "node"
              ? node.shapefileNodeId
              : node.projectShapefileId
              ? node.projectShapefileId
              : node.shapefileId,
          title: node.title,
          checked: false,
          children: [],
          source: source,
          fileType: ShapefileFileType.ShapefileLower,
          className: node.children ? "shapefile-folder" : "shapefile-leaf",
          order: node.order
        };

        if (treeNode.type === "folder") {
          if (
            source !== SHAPEFILE_SOURCE.ORG &&
            node.shapefileNodeId === "orgLevelShapefiles"
          )
            return;

          treeNode.folder = {
            source: source
          };
          treeNode.shapefileNodeId = node.shapefileNodeId;
        } else if (treeNode.type === "layer") {
          const shapefile = node as IShapefile;
          const type =
            source === SHAPEFILE_SOURCE.ORG
              ? "orgShapefile"
              : source === SHAPEFILE_SOURCE.MCDAN
              ? "mcdanShapefile"
              : "projectShapefile";
          if (shapefile) {
            const sfnode = node as IShapefile;
            treeNode.layer = {
              name: node.title,
              type: type,
              selected: true,
              zoom: [0, 22],
              style: {
                color: shapefile.color,
                strokeColor: shapefile.strokeColor,
                thickness: shapefile.thickness,
                lineStyle: shapefile.lineStyle,
                fillPattern: shapefile.fillPattern,
                opacity: shapefile.opacity
              }
            };
            treeNode.projectId = node.projectId;
            treeNode.shapefileId = node.shapefileId;
            treeNode.projectShapefileId = node.projectShapefileId;
            treeNode.shapefileNodeId = node.shapefileNodeId;
            treeNode.checked = treeNode.layer.selected;
            treeNode.name = node.name;
            treeNode.properties = sfnode.properties;
            treeNode.fileType = sfnode.fileType;
            treeNode.rasterAttributes = sfnode.rasterAttributes;
            treeNode.tilesReady = sfnode.tilesReady;
            treeNode.tilesStatus = sfnode.tilesStatus;
            treeNode.disableCheckbox =
              sfnode.tilesStatus != null &&
              sfnode.tilesStatus !== "completed" &&
              sfnode.tilesStatus !== "failed";
            treeNode.renderingMode = sfnode.renderingMode;
          }
        }

        if (node.children) {
          treeNode.children = parseShapefileChildren(node.children, source);
        }
        subTree.push(treeNode);
      });
    }

    return subTree;
  };

  const parsePolygonBins = (
    source: string,
    geomFields = groupedBins
  ): ProjectTreeNodeT[] => {
    const subTree: ProjectTreeNodeT[] = [];
    if (geomFields) {
      geomFields.forEach((geomField) => {
        const geomFieldNode: ProjectTreeNodeT = {
          type: "layer",
          key: geomField?.id,
          title: geomField?.title,
          checked: false,
          source: source,
          ...(source === "project" && { projectId: selectedProjectId }),
          layer: {
            name: geomField?.id,
            type: "polygonBin",
            selected: false,
            zoom: [0, 22],
            style: {
              color: "#0000FF",
              strokeColor: "#transparent",
              thickness: 1,
              opacity: 1
            }
          }
        };
        subTree.push(geomFieldNode);
      });
      return subTree;
    }
  };

  const parsePolygonBinFolders = (
    source: string,
    bins = groupedBins
  ): ProjectTreeNodeT[] => {
    const subTree: ProjectTreeNodeT[] = [];
    if (bins) {
      bins.forEach((bin) => {
        const binNode: ProjectTreeNodeT = {
          type: "folder",
          key: `${source}_${bin?.key ?? bin?.id}`,
          title: bin?.key ?? bin?.title,
          checked: false,
          layer: {
            name: bin?.key ?? bin?.id,
            type: "polygonBin",
            selected: false,
            zoom: [0, 22],
            style: {
              color: "#0000FF",
              strokeColor: "#transparent",
              thickness: 1,
              opacity: 1
            }
          }
        };

        if (bin?.geomFields) {
          const filteredGeomFields = bin.geomFields.filter(
            (field) => field.source === source
          );
          if (filteredGeomFields.length > 0) {
            binNode.children = parsePolygonBins(source, filteredGeomFields);
            subTree.push(binNode);
          }
        }
      });
      return subTree;
    }
  };

  useEffect(() => {
    const tree: ProjectTreeNodeT[] = [];

    const dynamicProjectTreeRootMap = getProjectTreeRootMap(
      ipdbGeoModelLoaded,
      ipdbControlWellsLoaded,
      rigFracEnabled
    );

    Object.keys(dynamicProjectTreeRootMap).forEach((nodeKey) => {
      if (
        !activeEntityKinds.includes(EntityKind.Facility) &&
        nodeKey === FACILITY_LAYER_ROOT
      ) {
        return;
      }
      let source: SHAPEFILE_SOURCE = SHAPEFILE_SOURCE.MAP;
      switch (nodeKey) {
        case SHAPEFILE_TREE_SECTIONS.PROJECT:
          source = SHAPEFILE_SOURCE.PROJECT;
          break;
        case SHAPEFILE_TREE_SECTIONS.ORGANIZATION:
          source = SHAPEFILE_SOURCE.ORG;
          break;
        case SHAPEFILE_TREE_SECTIONS.MCDANIEL:
          source = SHAPEFILE_SOURCE.MCDAN;
          break;
      }

      const polygonBinsFolder = dynamicProjectTreeRootMap[nodeKey].includes(
        POLYGON_BINS_MY_FIELDS_LAYERS,
        POLYGON_BINS_ORG_FIELDS_LAYERS,
        POLYGON_BINS_PROJECT_FIELDS_LAYERS
      );

      // Disable checkbox for "Geo Model Layers" folder
      const isGeoModelFolder = nodeKey === GEO_MODEL_LAYERS;

      const toLayerNode = (mapKey: string): ProjectTreeNodeT => {
        // Disable checkbox for "Geo Model" layers (IPDB layers)
        const isGeoModelLayer =
          mapKey === IPDB_LAYER ||
          mapKey === IPDB_CONTROL_WELLS_LAYER ||
          isGeoModelFolder;

        return {
          type: polygonBinsFolder ? "folder" : "layer",
          key: mapKey,
          title: mapKey,
          checked: polygonBinsFolder ? false : true,
          disableCheckbox: isGeoModelLayer,
          tooltip: isGeoModelLayer ? GEO_MODEL_TOOLTIP_MESSAGE : undefined,
          layer: {
            name: mapKey,
            type: "map",
            selected: true,
            zoom: [0, 22]
          }
        };
      };

      // "Grid Layers" is the only section with a region folder between the
      // section and its layers. Every other section holds its layers directly.
      const children =
        nodeKey === GRID_LAYERS_SECTION
          ? GRID_LAYER_FOLDERS.map(
              (folder): ProjectTreeNodeT => ({
                type: "rootFolder",
                key: folder.title,
                title: folder.title,
                checked: false,
                source: source,
                children: folder.layers.map(toLayerNode)
              })
            )
          : dynamicProjectTreeRootMap[nodeKey].map(toLayerNode);

      tree.push({
        type: "rootFolder",
        key: nodeKey,
        title: `${nodeKey}`,
        checked: isGeoModelFolder ? true : false,
        source: source,
        disableCheckbox: isGeoModelFolder,
        tooltip: isGeoModelFolder ? GEO_MODEL_TOOLTIP_MESSAGE : undefined,
        children: children
      });
    });

    if (projectShapefiles.data?.Project) {
      tree.filter((x) => x.key === SHAPEFILE_TREE_SECTIONS.PROJECT)[0].children =
        parseShapefileChildren(
          projectShapefiles.data.Project.children,
          SHAPEFILE_SOURCE.PROJECT
        );

      tree.filter((x) => x.key === SHAPEFILE_TREE_SECTIONS.ORGANIZATION)[0].children =
        parseShapefileChildren(
          projectShapefiles.data.Organization.children,
          SHAPEFILE_SOURCE.ORG
        );

      tree.filter((x) => x.key === SHAPEFILE_TREE_SECTIONS.MCDANIEL)[0].children =
        parseShapefileChildren(
          projectShapefiles.data.McDaniel.children,
          SHAPEFILE_SOURCE.MCDAN
        );
    }

    if (groupedColumns) {
      tree
        .filter((x) => x.key === "Polygon Bins")[0]
        .children.filter((y) => y.key === "My Fields")[0].children =
        parsePolygonBinFolders("user");

      tree
        .filter((x) => x.key === "Polygon Bins")[0]
        .children.filter((y) => y.key === "Organization Fields")[0].children =
        parsePolygonBinFolders("org");
      tree
        .filter((x) => x.key === "Polygon Bins")[0]
        .children.filter((y) => y.key === "Project Fields")[0].children =
        parsePolygonBinFolders("project", groupedBins);
    }

    setTree(tree);
  }, [
    projectShapefiles.data,
    activeEntityKinds,
    columns,
    ipdbGeoModelLoaded,
    ipdbControlWellsLoaded,
    rigFracEnabled
  ]); // Re-run when shapefiles, columns, ipdb loading status, or rig/frac availability change

  return {
    tree,
    isLoading: projectShapefiles.isLoading,
    error: null
  };
}
